
import { Component, Emit, Mixins, Prop, Watch } from 'vue-property-decorator'
import MAddToCartButton from '@mg/web-components/Atoms/Buttons/AddToCartButton'
import Loader from '@mg/web-components/Atoms/Loader'
import {
  RoundedButton,
  RoundedButtonType,
  Icon,
  MALL_ICONS,
} from '@mg/micro/vue2'
import Counter from '@mg/web-components/Atoms/FormControls/Counter'
import { AddToCartButtonTranslations } from '@mg/web-components/Atoms/Buttons/AddToCartButton/types'

import MarxyTrackingMixin from '~/utils/marxy/MarxyTrackingMixin'
import { GET_CONFIG } from '~/graphql/gql/config'
import {
  GQL_LOCAL_GET_CART_ARTICLE_IDS,
  GQL_LOCAL_MUTATION_ADD_TOC_CART,
  GQL_LOCAL_MUTATION_AMOUNT_CART,
  GQL_LOCAL_MUTATION_DELETE_CART,
} from '~/graphql/gqlLocal/localCart'
import {
  GQLLocalCart,
  GQLLocalCartAddInput,
  GQLLocalCartArticle,
  GQLLocalMutationChangeAmountArgs,
  GQLLocalMutationDeleteCartArgs,
  GQLLocalUiInput,
} from '~/graphql/__generated__/interfacesLocal'
import {
  GQLBonusSet,
  GQLBonusSetVariantData,
  GQLConfig,
  GQLProduct,
} from '~/graphql/__generated__/interfaces'
import { GQL_LOCAL_MUTATION_SET_UI } from '~/graphql/gqlLocal/localUi'
import { GtmEventTypes } from '~/utils/tracking/gtm'
import { SelectedBundles } from '~/components/common.types'
import { YottlyEventTypes } from '~/utils/tracking/yottly'
import { AddToCartData, RemoveFromCartData } from '~/utils/tracking/gtm/cart'

const MAX_ATTEMPTS = 3
const SESSION_GTM_CS_CART = 'gtm_cs_cart'
const BUY_NOW_TEXT_LENGTH_BEFORE_SHRINK = 15

export enum ButtonSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

interface TooltipContent {
  amount: number
  title: string
  show: boolean
  shown: boolean
}

@Component({
  apollo: {
    cart: {
      fetchPolicy: 'cache-only',
      prefetch: false,
      query: GQL_LOCAL_GET_CART_ARTICLE_IDS,
    },
    getConfig: {
      fetchPolicy: 'cache-only',
      query: GET_CONFIG,
    },
  },
  components: {
    RoundedButton,
    Counter,
    Icon,
    Loader,
    MAddToCartButton,
  },
  mixins: [MarxyTrackingMixin],
})
export default class AddToCartButton extends Mixins(MarxyTrackingMixin) {
  @Prop({ type: Object }) product: GQLProduct
  @Prop({ default: false, type: Boolean }) mini: boolean
  @Prop({ type: Object }) bonusSet: GQLBonusSet
  @Prop({ default: ButtonSize.Small, type: String }) size: ButtonSize
  @Prop() selectedBundles: SelectedBundles
  @Prop({ type: Object }) translations: AddToCartButtonTranslations
  @Prop({ type: String }) context: string
  @Prop({ type: String }) billingMethodId: string
  @Prop({ default: true, type: Boolean }) showIcon: boolean
  @Prop({ default: false, type: Boolean }) showCounter: boolean
  loading: boolean = false

  cart: GQLLocalCart
  counterValue: number = 1
  errorCount: number = 0
  isLoaderActive = false
  getConfig: GQLConfig
  tooltipContents: TooltipContent[] = []
  MALL_ICONS = MALL_ICONS

  debounce: NodeJS.Timer

  get localTranslations() {
    return (
      this.translations || {
        added: this.$t('common.cta_added'),
        buy_now: this.$t('common.cta_buy_now'),
      }
    )
  }

  get shouldBeCounterVisible() {
    const countMall = this.product?.mainVariant?.availability?.countMall ?? 1
    return this.showCounter && this.product?.fmcgCategory && countMall > 1
  }

  get productFromCart() {
    const articles = this.cart?.articles
    if (articles === undefined) {
      return undefined
    }
    const itemId = this.bonusSet
      ? this.bonusSet.id
      : this.product.mainVariant.id

    return articles.find((article) => String(article.articleId) === itemId)
  }

  get cartObject() {
    return {
      amount: this.counterValue,
      billingMethodId: this.billingMethodId,
      bundles: this.selectedBundles,
      context: this.context,
      ...(!this.bonusSet ? { itemId: this.product.id } : {}),
      ...(!this.bonusSet ? { variantId: this.product.mainVariant.id } : {}),
      ...(this.bonusSet ? { bonusSetId: this.bonusSet.id } : {}),
    }
  }

  get isProductInCart() {
    if (!this.cart) {
      return false
    }
    return !!this.productFromCart
  }

  get realCounterValue() {
    const val = this.productFromCart?.count ?? this.counterValue
    return Number(val)
  }

  get counterMax() {
    const configMax = Number(
      this.getConfig.shopConfig.cartMaxArticleItems ?? '30'
    )
    const countMall = this.product.mainVariant.availability?.countMall ?? 1
    if (countMall <= 0) {
      return 1
    } else if (countMall > configMax) {
      return configMax
    }
    return this.product.mainVariant.availability.countMall
  }

  get tooltipContent() {
    if (!this.shouldBeCounterVisible) {
      return ''
    }
    return this.tooltipContents.reduce((acc, tooltip) => {
      return tooltip.amount < this.realCounterValue && tooltip.show
        ? `${acc} ${tooltip.title}<br/>`
        : acc
    }, '')
  }

  get add2CartFontSize() {
    // eslint-disable-next-line camelcase
    return this.localTranslations?.buy_now?.length >
      BUY_NOW_TEXT_LENGTH_BEFORE_SHRINK
      ? 'add-to-cart-shrink-text'
      : null
  }

  @Watch('product', { immediate: true })
  setInitialTooltipContent() {
    if (!this.shouldBeCounterVisible) {
      return
    }
    this.tooltipContents = [
      ...this.getTooltipPromotionContent(),
      ...this.getTooltipGiftContent(),
    ]
  }

  getTooltipPromotionContent() {
    if (
      this.shouldBeCounterVisible &&
      this.product?.mainVariant?.inPromotion &&
      this.product?.mainVariant?.discountPromotionAmount
    ) {
      return [
        {
          amount: this.product?.mainVariant?.discountPromotionAmount,
          show: true,
          shown: false,
          title: this.$translateWithParams('detail.detail_promotion_tooltip', [
            this.product?.mainVariant?.discountPromotionAmount,
          ]),
        },
      ]
    }
    return []
  }

  getTooltipGiftContent() {
    const product = this.product
    if (this.shouldBeCounterVisible && product?.mainVariant?.gift) {
      const gifts = product?.mainVariant?.giftDetails?.variants || []
      return gifts.map((gift) => {
        const amount =
          (gift.variant?.availability?.countMall ?? 0) +
          (gift.variant?.availability.countExternal ?? 0) +
          (gift.variant?.availability.countSupplier ?? 0)
        return {
          amount,
          show: true,
          shown: false,
          title: `${this.$translateWithParams('detail.detail_gift_tooltip', [
            amount,
          ])} - ${gift.variant?.title}`,
        }
      })
    }
    return []
  }

  toggleTooltipContentsVisibility(count: number) {
    this.tooltipContents.forEach((tooltip) => {
      if (count > tooltip.amount && tooltip.show) {
        if (tooltip.shown) {
          tooltip.show = false
        } else {
          tooltip.shown = true
        }
      }
      if (count <= tooltip.amount) {
        tooltip.shown = false
        tooltip.show = true
      }
      return tooltip
    })
  }

  get buttonMiniIcon() {
    return this.isProductInCart ? MALL_ICONS.CART_ADDED : MALL_ICONS.CART_ADD
  }

  get buttonMiniType() {
    return this.isProductInCart
      ? RoundedButtonType.SUCCESS
      : RoundedButtonType.PRIMARY
  }

  get isSearchSuggester() {
    return this.context === 'search.whisperer'
  }

  @Watch('isProductInCart')
  onIsProductInCartChange(inCart: boolean) {
    if (!inCart && this.shouldBeCounterVisible) {
      this.counterValue = 1
    }
  }

  onCountChangeDebounce(count: number) {
    if (this.shouldBeCounterVisible) {
      this.toggleTooltipContentsVisibility(count)
    }

    if (this.isProductInCart) {
      this.isLoaderActive = true
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        this.isLoaderActive = false
        this.onCountChange(this.product.mainVariant.id, count)
      }, 600)
    } else {
      this.onCountChange(this.product.mainVariant.id, count)
    }
  }

  gaArticle(article: GQLLocalCartArticle) {
    const { articleId, variants } = article

    if (!variants?.[0].variantId) {
      return [
        {
          id: articleId.toString(),
          quantity: article.count,
        },
      ]
    }

    return variants.map((variant) => ({
      id: variant.variantId.toString(),
      bonusSetId: articleId,
      quantity: 1,
    }))
  }

  async deleteCart(article: GQLLocalCartArticle) {
    const { articleId } = article

    const articles = this.gaArticle(article)

    this.$pushAnalyticsEvent(GtmEventTypes.RemoveFromCart, {
      articles,
    } as RemoveFromCartData)

    await this.$apollo.mutate({
      mutation: GQL_LOCAL_MUTATION_DELETE_CART,
      variables: {
        articleId,
        cartId: this.cart.id,
      } as GQLLocalMutationDeleteCartArgs,
    })

    this.$pushAnalyticsEvent(YottlyEventTypes.CartInteraction, {
      articles: this.cart.articles,
      id: articleId,
    })

    this.marxyTrackCartUpdate(articleId)
  }

  async onCountChange(
    articleId: GQLLocalCartArticle['articleId'],
    count: GQLLocalCartArticle['count']
  ) {
    if (this.isProductInCart) {
      await this.$apollo.mutate({
        mutation: GQL_LOCAL_MUTATION_AMOUNT_CART,
        variables: {
          amount: count,
          articleId,
          cartId: this.cart.id,
        } as GQLLocalMutationChangeAmountArgs,
      })
    } else {
      this.counterValue = count
    }
  }

  @Emit()
  async click() {
    this.loading = true

    const variantId = this.bonusSet
      ? this.bonusSet.id
      : this.product.mainVariant.id

    try {
      if (this.mini && this.isProductInCart && this.productFromCart) {
        await this.deleteCart(this.productFromCart)
        return
      }

      await this.$apollo.mutate({
        mutation: GQL_LOCAL_MUTATION_ADD_TOC_CART,
        variables: {
          addCart: this.cartObject as GQLLocalCartAddInput,
        },
      })

      if (!variantId) {
        this.$logger.error(
          'AddToCartButton.click - cart does not contain mainVariantId',
          {
            context: this.context,
          }
        )
      }

      this.gaEventSend(variantId)

      this.marxyTrackCartUpdate(variantId)

      this.$pushAnalyticsEvent(YottlyEventTypes.CartInteraction, {
        articles: this.cart.articles,
        id: variantId,
      })

      const context = `${this.$route.name}.${this.context}`

      this.setSessionForCart(variantId, context)

      this.errorCount = 0
      this.loading = false

      this.added()

      if (!this.isSearchSuggester) {
        await this.$apollo.mutate({
          mutation: GQL_LOCAL_MUTATION_SET_UI,
          variables: {
            ui: {
              openHeaderTab: null,
              openAddCart: variantId,
            } as GQLLocalUiInput,
          },
        })
      }
    } catch (error) {
      this.errorCount++
      if (this.errorCount < MAX_ATTEMPTS) {
        this.click()
      } else {
        this.errorCount = 0
        this.loading = false
        this.$logger.info(
          "AddCartToButton.click - Can't add to cart",
          error,
          this.cartObject
        )
      }
    } finally {
      this.loading = false
    }
  }

  @Emit()
  async added() {}

  setSessionForCart(variantId: string, context: string) {
    try {
      const gtmCsCartSession = sessionStorage.getItem(SESSION_GTM_CS_CART)
      const gtmCsCart = gtmCsCartSession ? JSON.parse(gtmCsCartSession) : {}

      const mergeGtmCsCart = {
        ...gtmCsCart,
        [variantId]: context,
      }

      sessionStorage.setItem(
        SESSION_GTM_CS_CART,
        JSON.stringify(mergeGtmCsCart)
      )
    } catch (error) {
      this.$logger.info(
        'AddToCartButton.setSessionForCart',
        error,
        this.cartObject
      )
    }
  }

  gaEventSend(variantId: string) {
    try {
      const context = `${this.$route.name}.${this.context}`
      const variantData = this.bonusSet?.variantData ?? []

      const article = {
        context,
        id: variantId,
        quantity: this.counterValue,
        selectedBundles: this.selectedBundles,
      }

      const articlesMap = variantData.map((variant: GQLBonusSetVariantData) => {
        return {
          ...article,
          id: variant.id,
          bonusSetId: variantId,
        }
      })

      const articles = variantData.length ? articlesMap : [article]

      this.$pushAnalyticsEvent(GtmEventTypes.AddToCart, {
        articles,
      } as AddToCartData)
    } catch (e) {
      this.$logger.error(`AddToCartButton.gaEventSend - add cart error ${e}`, e)
    }
  }
}
